import moment from 'moment-timezone';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  Checkbox,
  CustomThemeProvider,
  DateRangePicker,
  IconTooltip,
  Input,
  ModalUI,
  showNotification,
  Tabs,
  Toast,
  Tooltip,
  TooltipInteractionEnum,
  usePortal
} from 'sonata-react-components';

import { copyToClipboard } from '@/components/reporting/helpers/reporting.helpers';
import { createSharedReport } from '@/components/reporting/reporting.resources';
import { buildSharedReportLink, getSHA512Hash } from '@/helpers/shareReport/shareReport.helper';

import type { Moment } from 'moment-timezone';
import type { ComponentProps } from 'react';
import type { ButtonUIProps } from 'sonata-react-components';
import type { SharedReportType } from 'sonata-static-storage/lib/schemas/report/sharedReport.schema';

import './shareReport.scss';

import SharedReportList from './SharedReportList/SharedReportList';

type CreatedSharedReport = {
  _id: string;
  name: string;
};

type ModalUIHandle = {
  closeModal: () => void;
  openModal: () => void;
};

type Props = {
  type: Extract<SharedReportType, 'singleDriveToStore' | 'agregatedDriveToStore' | 'singleDooh'>;
  pois?: number[];
  lineItemIds: string[];
  buttonProps?: ButtonUIProps;
  showFullAccessOption?: boolean;
};

// react-dates (via DateRangePicker) resolves its own `moment` copy, distinct
// from moment-timezone here, so the two Moment types need a cast at the picker
// boundary. Derive the exact type the single-date picker expects.
type DatePickerMoment = NonNullable<Extract<ComponentProps<typeof DateRangePicker>, { singlePicker: true }>['date']>;

type TabState = 'create' | 'myLinks';

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 20;

const PASSWORD_RULES = {
  length: (text: string) => text.length >= PASSWORD_MIN_LENGTH && text.length <= PASSWORD_MAX_LENGTH,
  lowerCase: (text: string) => /[a-z]/.test(text),
  numberCharacter: (text: string) => /[0-9]/.test(text),
  specialCharacter: (text: string) => /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(text),
  upperCase: (text: string) => /[A-Z]/.test(text)
} as const;

type PasswordRuleKey = keyof typeof PASSWORD_RULES;

const PASSWORD_RULE_MESSAGES: Record<PasswordRuleKey, { defaultMessage: string; id: string }> = {
  length: {
    defaultMessage: 'Be between {length} and {maxLength} characters',
    id: 'driveToStore.sharedReport.passwordRules.length'
  },
  lowerCase: {
    defaultMessage: 'Contain a lowercase letter',
    id: 'driveToStore.sharedReport.passwordRules.lowerCase'
  },
  numberCharacter: {
    defaultMessage: 'Contain a number',
    id: 'driveToStore.sharedReport.passwordRules.numberCharacter'
  },
  specialCharacter: {
    defaultMessage: 'Contain a special character',
    id: 'driveToStore.sharedReport.passwordRules.specialCharacter'
  },
  upperCase: {
    defaultMessage: 'Contain an uppercase letter',
    id: 'driveToStore.sharedReport.passwordRules.upperCase'
  }
};

function getPasswordRuleState(password = ''): Record<PasswordRuleKey, boolean> {
  return {
    length: PASSWORD_RULES.length(password),
    lowerCase: PASSWORD_RULES.lowerCase(password),
    numberCharacter: PASSWORD_RULES.numberCharacter(password),
    specialCharacter: PASSWORD_RULES.specialCharacter(password),
    upperCase: PASSWORD_RULES.upperCase(password)
  };
}

function getPasswordRuleIconClassName(isPasswordEmpty: boolean, isValid: boolean): string {
  if (isPasswordEmpty) {
    return 'sharedReport__ruleIcon sharedReport__ruleIcon--pending fa-solid fa-dot';
  }
  return `sharedReport__ruleIcon ${isValid ? 'fas' : 'fa-regular'} fa-${isValid ? 'check-circle' : 'times-circle'}`;
}

export default function ShareReport({
  type,
  pois,
  lineItemIds,
  buttonProps,
  showFullAccessOption = true
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const passwordInputId = useId();
  const generateButtonId = useId();
  const nameInputId = useId();
  const expirationDateId = useId();
  const fullAccessCheckboxId = useId();
  const targetBody = usePortal('driveToStoreSharedReportModal');

  const modalRef = useRef<ModalUIHandle>(null);
  const [activeTab, setActiveTab] = useState<TabState>('create');
  const [password, setPassword] = useState<string | undefined>();
  const [fullAccess, setFullAccess] = useState(false);
  const [lastSharedReportId, setLastSharedReportId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [name, setName] = useState(() =>
    formatMessage(
      { defaultMessage: 'Shared report - {date}', id: 'driveToStore.sharedReport.defaultName' },
      { date: moment().format('DD MMM, YYYY - HH:mm') }
    )
  );
  const [expirationDate, setExpirationDate] = useState<Moment>(() =>
    moment.utc().add(7, 'days').set({ hour: 23, minute: 59, second: 59 })
  );
  const [dateFocused, setDateFocused] = useState(false);

  const passwordRuleState = useMemo(() => getPasswordRuleState(password), [password]);
  const isPasswordValid = useMemo(() => Object.values(passwordRuleState).every(Boolean), [passwordRuleState]);

  const openModal = useCallback(() => {
    modalRef.current?.openModal();
  }, []);

  const resetState = useCallback(() => {
    setName(
      formatMessage(
        { defaultMessage: 'Shared report - {date}', id: 'driveToStore.sharedReport.defaultName' },
        { date: moment().format('DD MMM, YYYY - HH:mm') }
      )
    );
    setPassword(undefined);
    setFullAccess(false);
    setLoading(false);
    setShowCopiedToast(false);
    setShowErrorToast(false);
    setActiveTab('create');
    setLastSharedReportId(undefined);
  }, []);

  const onChangeTab = useCallback((id: string) => {
    setActiveTab(id as TabState);
    if (id === 'create') {
      setLastSharedReportId(undefined);
    }
  }, []);

  const generateLink = useCallback(async () => {
    if (!isPasswordValid || loading) {
      return;
    }
    setLoading(true);
    setShowErrorToast(false);
    setShowCopiedToast(false);
    try {
      const hashPassword = await getSHA512Hash(password!);
      const created: CreatedSharedReport = await createSharedReport(hashPassword, lineItemIds, expirationDate, name, {
        fullAccess,
        pois,
        type
      });
      const generatedLink = buildSharedReportLink(created._id);
      copyToClipboard(generatedLink);
      resetState();
      setLastSharedReportId(created._id);
      setActiveTab('myLinks');
      showNotification(
        'success',
        formatMessage(
          {
            defaultMessage: 'Success! Your link for "{name}" has been copied.',
            id: 'sharedReport.list.shareLink.generatedMessage'
          },
          {
            name: created.name
          }
        ) as string,
        {
          closable: true,
          icon: 'fal fa-check-circle'
        }
      );
    } catch {
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  }, [lineItemIds, expirationDate, fullAccess, isPasswordValid, loading, name, password, pois, type]);

  const closeToast = useCallback(() => {
    setShowCopiedToast(false);
    setShowErrorToast(false);
  }, []);

  const generateLinkButton = (
    <ButtonUI
      disabled={!isPasswordValid}
      icon="fal fa-arrow-right"
      iconPosition="right"
      id={generateButtonId}
      loading={loading}
      onClick={generateLink}
      shape="squareRounded"
      size="medium"
      text={formatMessage({
        defaultMessage: 'Generate link',
        id: 'shareReport.shareLink.generateLink'
      })}
    />
  );

  const modal = ReactDOM.createPortal(
    <CustomThemeProvider themeContrast="light">
      <ModalUI
        className="sharedReport"
        closeModalCallback={resetState}
        description={formatMessage({
          defaultMessage: 'Manage your active links or create a new one.',
          id: 'shareReport.shareLink.description'
        })}
        maxHeight="752px"
        ref={modalRef}
        title={formatMessage({
          defaultMessage: 'Share Report',
          id: 'shareReport.shareLink.title'
        })}
        width="714px"
      >
        <div className="sharedReport__container">
          <Tabs
            className="sharedReport__tabs"
            onChangeTab={onChangeTab}
            showDeleteAction={false}
            tabSelected={activeTab}
            tabs={[
              {
                component: (
                  <>
                    {(showCopiedToast || showErrorToast) && (
                      <Toast
                        autoClose={false}
                        closeButton
                        closeToast={closeToast}
                        icon={`fal fa-${showErrorToast ? 'exclamation-triangle' : 'check-circle'}`}
                        text={
                          showErrorToast
                            ? formatMessage({
                                defaultMessage: "The link couldn't be generated, please try again",
                                id: 'shareReport.shareLink.errorToast'
                              })
                            : formatMessage({
                                defaultMessage: 'The link has been copied correctly',
                                id: 'shareReport.shareLink.copiedToast'
                              })
                        }
                        type={showErrorToast ? 'danger' : 'success'}
                      />
                    )}
                    <div className="sharedReport__details">
                      <span className="sharedReport__detailsTitle">
                        {formatMessage({
                          defaultMessage:
                            "Add a password and click “Generate link”. You'll be able to copy it directly from your list.",
                          id: 'shareReport.shareLink.detailsTitle'
                        })}
                      </span>
                      <div className="sharedReport__nameAndDateRow">
                        <Input
                          className="sharedReport__nameInput"
                          id={nameInputId}
                          label={formatMessage({ defaultMessage: 'Link name', id: 'shareReport.shareLink.linkName' })}
                          name="sharedReportNameInput"
                          onChange={value => setName(typeof value === 'string' ? value : '')}
                          shape="squareUI"
                          type="text"
                          value={name}
                        />
                        <div className="sharedReport__expiration">
                          <DateRangePicker
                            block
                            date={expirationDate as DatePickerMoment}
                            displayFormat="DD MMM, YYYY"
                            focused={dateFocused}
                            id={expirationDateId}
                            label={formatMessage({
                              defaultMessage: 'Expiration date',
                              id: 'shareReport.shareLink.expirationDate'
                            })}
                            noBorder
                            numberOfMonths={1}
                            onDateChange={(date: DatePickerMoment | null) => {
                              if (date) {
                                setExpirationDate(date as Moment);
                              }
                            }}
                            onFocusChange={({ focused }: { focused: boolean | null }) => setDateFocused(!!focused)}
                            readOnly
                            singlePicker
                          />
                        </div>
                      </div>
                      <div className="sharedReport__passwordRow">
                        <Input
                          className="sharedReport__passwordInput"
                          id={passwordInputId}
                          label={formatMessage({
                            defaultMessage: 'Password',
                            id: 'shareReport.shareLink.password'
                          })}
                          maxNumberCharacters={PASSWORD_MAX_LENGTH}
                          name="sharedReportPasswordInput"
                          notFloatingPlaceholder
                          onChange={value => setPassword(typeof value === 'string' ? value : undefined)}
                          passwordVisibilityIcon
                          placeholder={formatMessage({
                            defaultMessage: 'Write password',
                            id: 'shareReport.shareLink.passwordPlaceholder'
                          })}
                          shape="squareUI"
                          type="password"
                          value={password}
                        />
                      </div>
                    </div>
                    <div className="sharedReport__hints">
                      <span className="sharedReport__hintsTitle">
                        {formatMessage({
                          defaultMessage: 'Password must:',
                          id: 'shareReport.shareLink.passwordHintsTitle'
                        })}
                      </span>
                      <div className="sharedReport__hintsRules">
                        {(Object.keys(passwordRuleState) as PasswordRuleKey[]).map(ruleKey => {
                          const isValid = passwordRuleState[ruleKey];
                          return (
                            <div
                              className={`sharedReport__rule sharedReport__rule--${isValid ? 'valid' : !password ? 'pending' : 'invalid'}`}
                              key={ruleKey}
                            >
                              <span className={getPasswordRuleIconClassName(!password, isValid)} />
                              <span
                                className={`sharedReport__ruleText sharedReport__ruleText--${isValid ? 'valid' : !password ? 'pending' : 'invalid'}`}
                              >
                                {formatMessage(PASSWORD_RULE_MESSAGES[ruleKey], {
                                  length: PASSWORD_MIN_LENGTH,
                                  maxLength: PASSWORD_MAX_LENGTH
                                })}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {showFullAccessOption && (
                      <div className="sharedReport__fullAccessRow">
                        <Checkbox
                          active={fullAccess}
                          id={fullAccessCheckboxId}
                          labelText={formatMessage({
                            defaultMessage: 'Share full view access',
                            id: 'shareReport.shareLink.fullAccess'
                          })}
                          name="sharedReportFullAccessCheckbox"
                          onClickAction={(_id: any, checked: boolean) => setFullAccess(checked)}
                        />
                        <IconTooltip
                          icon="fa-regular fa-circle-info"
                          iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
                          place="top"
                        >
                          <span>
                            {formatMessage({
                              defaultMessage:
                                'Users with this link will also be able to access individual footfall reports.',
                              id: 'shareReport.shareLink.fullAccessTooltip'
                            })}
                          </span>
                        </IconTooltip>
                      </div>
                    )}
                    <div className="sharedReport__linkButtonGenerator">
                      {isPasswordValid ? (
                        generateLinkButton
                      ) : (
                        <Tooltip
                          config={{
                            arrow: true,
                            interactions: [TooltipInteractionEnum.hover],
                            placement: 'top',
                            strategy: 'fixed',
                            text: formatMessage({
                              defaultMessage: 'Your password must meet all requirements',
                              id: 'shareReport.shareLink.generateLinkPasswordTooltip'
                            })
                          }}
                        >
                          <span className="sharedReport__disabledActionWrapper">{generateLinkButton}</span>
                        </Tooltip>
                      )}
                    </div>
                  </>
                ),
                icon: {
                  content: 'fa-solid fa-plus',
                  position: 'left'
                },
                id: 'create',
                title: formatMessage({ defaultMessage: 'Create Link', id: 'shareReport.shareLink.tabs.create' })
              },
              {
                component: (
                  <SharedReportList
                    lineItemIds={lineItemIds}
                    reloadToken={lastSharedReportId}
                    sharedReportId={lastSharedReportId}
                    type={type}
                  />
                ),
                icon: {
                  content: 'fa-solid fa-share-nodes',
                  position: 'left'
                },
                id: 'myLinks',
                title: formatMessage({ defaultMessage: 'My Links', id: 'shareReport.shareLink.tabs.myLinks' })
              }
            ]}
            variant="underlined"
          />
        </div>
      </ModalUI>
    </CustomThemeProvider>,
    targetBody
  );

  return (
    <>
      <ButtonUI {...buttonProps} onClick={openModal} />
      {modal}
    </>
  );
}
